import { Box } from '@mui/material';
import SortableContext from '../../../../../components/DragAndDrop/SortableContext';
import {
  OnDragEndParams,
  SortableStrategy,
} from '../../../../../components/DragAndDrop/dragAndDrop.types';
import LangEnum from '../../../../../lang/LangEnum';
import { CatalogBannerPreview, CatalogMediaDimensions } from '../../catalogCreationForm.types';
import { CATALOG_BANNERS_MAX_ITEMS } from '../../catalogCreationMedia.consts';
import { useCatalogMediaPreview } from '../../hooks/useCatalogMediaPreview';
import CatalogCreationMediaDropzone from '../CatalogCreationMediaDropzone/CatalogCreationMediaDropzone';
import CatalogCreationMediaGroup from '../CatalogCreationMediaGroup';
import CatalogMediaPreviewDialog from '../CatalogMediaPreviewDialog';
import CatalogBannerSortableTile from './CatalogBannerSortableTile';

interface CatalogBannerGalleryProps {
  label: LangEnum;
  dimensions: CatalogMediaDimensions;
  tileSize?: number;
  isFluid?: boolean;
  previews: CatalogBannerPreview[];
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (key: number) => void;
  onReplaceFile?: (key: number, file: File) => void;
  onMoveFile: (oldIndex: number, newIndex: number) => void;
}

const CatalogBannerGallery = ({
  label,
  dimensions,
  tileSize,
  isFluid,
  previews,
  onAddFiles,
  onRemoveFile,
  onReplaceFile,
  onMoveFile,
}: CatalogBannerGalleryProps) => {
  const { selectedUrl, openPreview, closePreview } = useCatalogMediaPreview();

  const previewAspectRatio = `${dimensions.width} / ${dimensions.height}`;

  const sortableItems = previews.map((preview) => ({ ...preview, id: String(preview.key) }));

  const renderTile = (preview: CatalogBannerPreview) => (
    <CatalogCreationMediaDropzone
      label={label}
      tileSize={isFluid ? '100%' : tileSize}
      previewAspectRatio={previewAspectRatio}
      isUploading={preview.isUploading}
      preview={preview.url ? { url: preview.url, fileName: preview.fileName } : null}
      isEditable={Boolean(onReplaceFile)}
      onSelectFiles={([file]) => onReplaceFile?.(preview.key, file)}
      onRemove={() => onRemoveFile(preview.key)}
      onPreview={() => openPreview(preview.url)}
    />
  );

  const handleDragEnd = ({ oldIndex, newIndex }: OnDragEndParams) => {
    onMoveFile(oldIndex, newIndex);
  };

  return (
    <CatalogCreationMediaGroup
      label={label}
      dimensions={dimensions}
      maxItems={CATALOG_BANNERS_MAX_ITEMS}
      tileSize={tileSize}
      isFluid={isFluid}
    >
      <SortableContext
        items={sortableItems}
        uniqueIdentifier="id"
        strategy={SortableStrategy.GRID}
        onDragEnd={handleDragEnd}
        onUpdateItems={() => undefined}
        OverlayComponent={({ activeIndex }) => (
          <Box sx={{ width: '100%' }}>{renderTile(previews[activeIndex])}</Box>
        )}
      >
        {previews.map((preview) => (
          <CatalogBannerSortableTile key={preview.key} id={String(preview.key)}>
            {renderTile(preview)}
          </CatalogBannerSortableTile>
        ))}
      </SortableContext>

      {previews.length < CATALOG_BANNERS_MAX_ITEMS && (
        <CatalogCreationMediaDropzone
          isMultiple
          label={label}
          tileSize={isFluid ? '100%' : tileSize}
          previewAspectRatio={previewAspectRatio}
          isUploading={false}
          onSelectFiles={onAddFiles}
          onRemove={() => undefined}
        />
      )}

      <CatalogMediaPreviewDialog
        urls={previews.map(({ url }) => url)}
        selectedUrl={selectedUrl}
        onClose={closePreview}
      />
    </CatalogCreationMediaGroup>
  );
};

export default CatalogBannerGallery;
